import {
  Component,
  OnDestroy,
  afterNextRender,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { TodayAlertsComponent } from '../../today-alerts/today-alerts.component';
import { TrendsComponent } from '../../trends/trends.component';
import { PowercutAndSummaryComponent } from '../../powercut-and-summary/powercut-and-summary.component';
import { PlantDetailsComponent } from '../../plant-details/plant-details.component';
import { TranslateModule } from '@ngx-translate/core';
import { RadialBarComponent } from '../../radial-bar/radial-bar.component';
import { Subject, takeUntil } from 'rxjs';
import { SiService } from '../../../../system-integrator/services/si.service';
import { BaseComponent } from '../../../../base/components/base.component';
import { TodayAlertDetails } from '../../../models/today-alert-details';
import { TotalPlantsSI } from '../../../models/total-plants-si';
import { AllPlantDetails } from '../../../models/all-plant-details';
import { PlantHealthDetails } from '../../../models/plant-health-details';
import {
  TodayPowerCutDetails,
  TodayPowerCutDetailsData,
} from '../../../models/today-power-cut-details';
import { CommonModule } from '@angular/common';
import { convertValueToBoundary } from '../../../utils/common-utils';

@Component({
    selector: 'app-non-solar-dashboard',
    templateUrl: './non-solar-dashboard.component.html',
    styleUrl: './non-solar-dashboard.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TodayAlertsComponent,
        TrendsComponent,
        PowercutAndSummaryComponent,
        PlantDetailsComponent,
        TranslateModule,
        RadialBarComponent,
        CommonModule,
    ]
})
export class NonSolarDashboardComponent
  extends BaseComponent
  implements OnDestroy
{
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  private readonly siService = inject(SiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private installerId = '';
  
  // Initialize with default values to prevent undefined/NA errors
  powerCutDetailsAndSummary: TodayPowerCutDetailsData = {
    backupCapacityPercentage: 0
  } as TodayPowerCutDetailsData;
  
  nonSolarPlant: {
    totalPlant: number;
    healthyPlant: number;
    unhealthyPlant: number;
    healthyPlantPercent: number;
  } = {
    totalPlant: 0,
    healthyPlant: 0,
    unhealthyPlant: 0,
    healthyPlantPercent: 0
  };
  
  isData = new Subject<boolean>();
  private readonly destroy$ = new Subject<void>();

  constructor() {
    super();
    
    // Angular v21: Use afterNextRender for post-render initialization
    afterNextRender(() => {
      this.baseUserService.sessionService.startBnWatch(this.router);
      this.installerId = this.authService.loggedUser?.installer_id || '';

      /**
       * Timer listener
       */
      this.baseUserService.timerService
        .getTimerObservable()
        .pipe(takeUntil(this.destroy$))
        .subscribe((fetchData) => {
          if (fetchData) {
            this.fetchApiData();
          }
        });

      // Fetch initial data
      this.fetchApiData();
    });
  }
  fetchApiData() {
    this.resolveAllSubscriptions([
      this.fetchTotalNonSolarPlantDetails(),
      this.fetchTodayNonSolarPowerCutDetails(),
    ]);
    this.loadData(
      [
        this.baseUserService.todayAlertsLoading,
        this.baseUserService.trendsLoading,
        this.baseUserService.powerCutLoading,
        this.baseUserService.plantDetailsLoading,
      ],
      this.isData,
    );
  }

  /**
   * Fetch total plant details
   */
  fetchTotalNonSolarPlantDetails() {
    let loaderSubject = new Subject<boolean>();
    this.siService
      .getTotalPlantsSI(this.installerId, '3')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (value: TotalPlantsSI) => {
          if (value.data != null) {
            this.nonSolarPlant.totalPlant = value.data.totalPlants;
            this.cdr.markForCheck();
            this.fetchNonSolarPlantHealthDetails();
          }
          loaderSubject.next(true);
        },
        error: (e: Error) => {
          this.logger.error('Non-solar plant fetch error:', e.message);
          loaderSubject.next(true);
        },
        complete: () => loaderSubject.complete(),
      });
    return loaderSubject;
  }

  /**
   * Fetch non-solar today alerts detail
   */
  fetchTodayAlertsDetail() {
    let loaderSubject = new Subject<boolean>();
    this.siService
      .getTodayAlertDetails(this.installerId, '3')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (value: TodayAlertDetails) => {
          if (value.data != null) {
            this.baseUserService.todayAlerts.next(value.data);
            this.cdr.markForCheck();
          }
          loaderSubject.next(true);
        },
        error: (e: Error) => {
          this.logger.error('Today alerts fetch error:', e.message);
          loaderSubject.next(true);
        },
        complete: () => loaderSubject.complete(),
      });
    return loaderSubject;
  }

  /**
   * Fetch non-solar plant details
   */
  fetchNonSolarPlantDetail() {
    this.siService
      .getAllPlantDetails(this.installerId, '3')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (value: AllPlantDetails) => {},
        error: (e: Error) => {
          this.logger.error('Plant details fetch error:', e.message);
        },
      });
  }

  /**
   * Fetch non-solar plant health details
   */
  fetchNonSolarPlantHealthDetails() {
    let loaderSubject = new Subject<boolean>();

    this.siService
      .getPlantHealthDetails(this.installerId, '3')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (value: PlantHealthDetails) => {
          const healthPercent = Math.floor(value.data.nonSolarPlantsHealth);
          const totalPlant = this.nonSolarPlant.totalPlant;
          
          this.nonSolarPlant.healthyPlantPercent = healthPercent;
          if (totalPlant != null) {
            this.nonSolarPlant.healthyPlant = Math.round(
              totalPlant * (healthPercent / 100),
            );
            this.nonSolarPlant.unhealthyPlant =
              totalPlant - this.nonSolarPlant.healthyPlant;
          }
          this.cdr.markForCheck();
          loaderSubject.next(true);
        },
        error: (error) => {
          this.logger.error('Plant health fetch error:', error);
          loaderSubject.next(true);
        },
        complete: () => loaderSubject.complete(),
      });
    return loaderSubject;
  }

  /**
   * Fetch today power cut details
   */
  fetchTodayNonSolarPowerCutDetails() {
    let loaderSubject = new Subject<boolean>();
    this.siService
      .getTodayPowerCutDetails(this.installerId, '3')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (value: TodayPowerCutDetails) => {
          /*
          set battery progress bar width
          */
          if (value.data) {
            this.powerCutDetailsAndSummary = value.data;
            this.cdr.markForCheck();
          }

          loaderSubject.next(true);
        },
        error: (e: Error) => {
          this.logger.error('Power cut details fetch error:', e.message);
          loaderSubject.next(true);
        },
        complete: () => loaderSubject.complete(),
      });
    return loaderSubject;
  }

  calculateProgress(arg0?: number): string {
    let pixel = convertValueToBoundary(arg0 ?? 0, 0, 100, 0, 60);
    return pixel + 'px';
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.destroy$.next();
    this.destroy$.complete();
    this.baseUserService.sessionService.stopBnTimer();
  }
}
